// /Frontend/src/pages/CompanyAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import BillingTab from '../components/BillingTab';
import { useNavigate } from 'react-router-dom';
import {
  Server, LogOut, Settings, Building2,
  CheckCircle, XCircle, Clock, Users,
  Activity, Shield, Package, CreditCard,
  DollarSign, AlertCircle, X, TrendingUp,
  PauseCircle, RefreshCw, Trash2, FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import HomeButton from '../components/HomeButton';
import { PlusCircle, KeyRound } from 'lucide-react';
import AddAppModal from '../components/AddAppModal';
import CreateSecretsModal from '../components/CreateSecretsModal';
import { deleteDeployment } from '../services/api';

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:    { color: 'bg-green-500/10 text-green-400 border-green-500/20',    label: 'Active' },
    pending:   { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Pending' },
    rejected:  { color: 'bg-red-500/10 text-red-400 border-red-500/20',          label: 'Rejected' },
    suspended: { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20',    label: 'Suspended' },
    paused:    { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Paused' },
    cancelled: { color: 'bg-red-500/10 text-red-400 border-red-500/20',          label: 'Cancelled' },
  };
  const s = map[status] || map.active;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.color}`}>
      {s.label}
    </span>
  );
};

// ─── Plan badge ───────────────────────────────────────────────────────────────
const PlanBadge = ({ slug }) => {
  const map = {
    'web-hosting':       'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'namespace':         'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'managed-app':       'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'dedicated-cluster': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${map[slug] || 'bg-slate-500/10 text-slate-400 border-slate-500/20'} capitalize`}>
      {slug?.replace(/-/g, ' ') || 'Unknown'}
    </span>
  );
};

// ─── Logs modal ────────────────────────────────────────────────────────────
const LogsModal = ({ appName, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/user/deployments/${encodeURIComponent(appName)}/logs`);
      setLogs(res.data.logs || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appName]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Logs</h3>
            <p className="text-xs text-slate-400 font-mono">{appName}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-black/40 border border-slate-800 rounded-lg p-4 font-mono text-xs">
          {loading && logs.length === 0 && (
            <p className="text-slate-500">Loading logs...</p>
          )}
          {error && (
            <div className="flex items-start gap-2 text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
          {!error && !loading && logs.length === 0 && (
            <p className="text-slate-500">No log output available.</p>
          )}
          {logs.map((line, i) => (
            <div key={i} className="text-slate-300 whitespace-pre-wrap break-all leading-relaxed">
              {line}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Quota panel ──────────────────────────────────────────────────────────
// Renders whatever keys the backend returns (requests.memory, limits.memory,
// requests.storage, etc.) generically — no changes needed here when a new
// quota key (like storage) is added to the namespace's YAML.
const QUOTA_LABELS = {
  'requests.memory': 'Memory (Requests)',
  'limits.memory': 'Memory (Limits)',
  'requests.storage': 'Storage',
  'requests.cpu': 'CPU (Requests)',
  'limits.cpu': 'CPU (Limits)',
  'persistentvolumeclaims': 'PVC Count',
};

const QuotaBar = ({ label, entry }) => {
  const pct = entry.percentUsed;
  const barColor = pct == null ? 'bg-slate-600' : pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-blue-500';
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <span className="text-xs text-slate-400">
          {entry.usedDisplay} / {entry.hardDisplay ?? 'N/A'}
        </span>
      </div>
      {pct != null && (
        <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden mb-2">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {entry.remainingDisplay && (
        <p className="text-xs text-slate-500">{entry.remainingDisplay} remaining</p>
      )}
    </div>
  );
};

const QuotaPanel = ({ quota, quotaMessage }) => {
  if (!quota) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 text-slate-400">
          <Package className="w-4 h-4" />
          <p className="text-sm">{quotaMessage || 'No resource quota configured yet'}</p>
        </div>
      </div>
    );
  }

  const entries = Object.entries(quota.quota);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-400" />
          Namespace Resource Quota
        </h3>
        <span className="text-xs text-slate-500 font-mono">{quota.namespace}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {entries.map(([key, entry]) => (
          <QuotaBar key={key} label={QUOTA_LABELS[key] || key} entry={entry} />
        ))}
      </div>
    </div>
  );
};

// ─── Confirm delete modal ──────────────────────────────────────────────────
// Requires typing the exact app name before the Delete button enables —
// this action force-deletes the ArgoCD Application and removes its
// manifest from GitHub, so a simple browser confirm() isn't enough of a
// safeguard against a misclick.
const ConfirmDeleteModal = ({ appName, onClose, onConfirm, deleting }) => {
  const [typedName, setTypedName] = useState('');
  const matches = typedName === appName;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            Delete "{appName}"?
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-slate-400 mb-4">
          This force-deletes the app from ArgoCD (removing its live Kubernetes resources) and
          removes its manifest from GitHub. <span className="text-red-400 font-medium">This
          cannot be undone.</span>
        </p>

        <label className="block text-xs text-slate-500 mb-1">
          Type <span className="font-mono text-slate-300">{appName}</span> to confirm
        </label>
        <input
          type="text"
          autoFocus
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder={appName}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono mb-4 focus:outline-none focus:border-red-500"
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches || deleting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {deleting
              ? <div className="w-3.5 h-3.5 border border-red-400/30 border-t-red-400 rounded-full animate-spin" />
              : <Trash2 className="w-3.5 h-3.5" />
            }
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const CompanyAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [pendingUsers, setPendingUsers]       = useState([]);
  const [allUsers, setAllUsers]               = useState([]);
  const [deployments, setDeployments]         = useState([]);
  const [stats, setStats]                     = useState({ totalDeployments: 0, activeServers: 0, uptime: 99.9 });
  const [subscriptions, setSubscriptions]     = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [activeTab, setActiveTab]             = useState('overview');
  const [cancellingId, setCancellingId]       = useState(null);
  const [deletingUserId, setDeletingUserId]   = useState(null);

  const [syncingApp, setSyncingApp]     = useState(null);
  const [syncError, setSyncError]       = useState('');
  const [logsAppName, setLogsAppName]   = useState(null);
  const [deletingApp, setDeletingApp]   = useState(null);
  const [deleteConfirmApp, setDeleteConfirmApp] = useState(null);

  const [quota, setQuota]               = useState(null);
  const [quotaMessage, setQuotaMessage] = useState('');
  const [showAddApp, setShowAddApp] = useState(false);
  const [showCreateSecrets, setShowCreateSecrets] = useState(false);

  // Brief toast shown after Add New App / Add Deployment / Create Secrets
  // succeed — reuses the same red banner slot styling as syncError below,
  // just in a neutral/green color instead.
  const [actionMessage, setActionMessage] = useState('');

  const fetchData = async () => {
    try {
      const [pendingRes, usersRes, deploymentsRes, statsRes, subsRes, quotaRes] = await Promise.all([
        api.get('/company-admin/pending'),
        api.get('/company-admin/users'),
        api.get('/monitoring/services'),
        api.get('/user/stats'),
        api.get('/company-admin/subscriptions'),
        api.get('/company-admin/quota'),
      ]);
      setPendingUsers(pendingRes.data.users || []);
      setAllUsers(usersRes.data.users || []);
      setDeployments(deploymentsRes.data.services || []);
      setStats(statsRes.data || { totalDeployments: 0, activeServers: 0, uptime: 99.9 });
      setSubscriptions(subsRes.data.subscriptions || []);
      setQuota(quotaRes.data.quota || null);
      setQuotaMessage(quotaRes.data.message || '');
    } catch (err) {
      console.error('Error fetching company admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (userId) => {
    try {
      await api.post(`/company-admin/users/${userId}/approve`);
      const approved = pendingUsers.find(u => u.id === userId);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      if (approved) setAllUsers(prev => [...prev, { ...approved, status: 'active' }]);
    } catch (err) {
      console.error('Error approving user:', err);
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.post(`/company-admin/users/${userId}/reject`);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error('Error rejecting user:', err);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Permanently delete user "${userName}"? This cannot be undone.`)) return;
    setDeletingUserId(userId);
    try {
      await api.delete(`/company-admin/users/${userId}`);
      setAllUsers(prev => prev.filter(u => u.id !== userId));
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCancelSubscription = async (id, planName) => {
    if (!window.confirm(`Cancel the ${planName} subscription? This cannot be undone.`)) return;
    setCancellingId(id);
    try {
      await api.post(`/company-admin/subscriptions/${id}/cancel`);
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'cancelled' } : s));
    } catch (err) {
      console.error('Error cancelling subscription:', err);
    } finally {
      setCancellingId(null);
    }
  };

  const handleSync = async (appName) => {
    setSyncingApp(appName);
    setSyncError('');
    try {
      await api.post(`/user/deployments/${encodeURIComponent(appName)}/sync`);
      setTimeout(() => {
        fetchData();
      }, 1500);
    } catch (error) {
      console.error('Error syncing app:', error);
      setSyncError(error.response?.data?.message || `Failed to sync ${appName}`);
    } finally {
      setSyncingApp(null);
    }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const handleDeleteDeployment = async (appName) => {
    setDeletingApp(appName);
    setSyncError('');
    try {
      await deleteDeployment(appName);
      setDeployments(prev => prev.filter(d => d.name !== appName));
      setDeleteConfirmApp(null);
    } catch (err) {
      console.error('Error deleting deployment:', err);
      setSyncError(err.response?.data?.message || `Failed to delete ${appName}`);
      setDeleteConfirmApp(null);
    } finally {
      setDeletingApp(null);
    }
  };

  // Shared success handler for the three new modals — refresh dashboard
  // data (stats, deployments, quota) and show a brief confirmation, same
  // pattern as handleSync's post-action refresh.
  const handleProvisioned = (message) => {
    setActionMessage(message);
    fetchData();
    setTimeout(() => setActionMessage(''), 5000);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':     return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'degraded':    return 'bg-red-500/10 text-red-400 border border-red-500/20';
      case 'progressing': return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
      case 'missing':     return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      default:            return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
    }
  };

  const getSyncStatusColor = (syncStatus) => {
    switch (syncStatus?.toLowerCase()) {
      case 'synced':    return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'outofsync': return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      default:          return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  const activeUsers    = allUsers.filter(u => u.status === 'active').length;
  const activeSubCount = subscriptions.filter(s => s.status === 'active').length;
  const monthlySpend   = subscriptions
    .filter(s => s.status === 'active' && s.billing_cycle === 'monthly')
    .reduce((sum, s) => sum + parseFloat(s.price_total || 0), 0)
    .toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
       <HomeButton />
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-400 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{user?.company || 'Company'} Dashboard</h1>
                <p className="text-xs text-slate-400">Welcome back, {user?.firstName || 'Admin'} · Company Admin</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-slate-400 hover:text-white transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {actionMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
            <p className="text-green-400 text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              {actionMessage}
            </p>
            <button onClick={() => setActionMessage('')} className="text-green-400 hover:text-green-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ── App deployment quick actions ─────────────────────────────── */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => setShowAddApp(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 rounded-lg transition-colors text-sm font-medium"
          >
            <PlusCircle className="w-4 h-4" />
            Add New App
          </button>
          <button
            onClick={() => setShowCreateSecrets(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 rounded-lg transition-colors text-sm font-medium"
          >
            <KeyRound className="w-4 h-4" />
            Create Secrets
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-2xl font-bold">{stats.totalDeployments}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Deployments</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <span className="text-2xl font-bold">{activeUsers}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Active Users</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <span className="text-2xl font-bold">{pendingUsers.length}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Pending Approvals</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-2xl font-bold">{activeSubCount}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Active Plans</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-2xl font-bold">${monthlySpend}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Monthly Spend</h3>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-6 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
          {[
            { key: 'overview',      label: 'Deployments',   icon: Server,      count: deployments.length },
            { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard,  count: subscriptions.length },
            { key: 'users',         label: 'Users',         icon: Users,       count: allUsers.length },
            { key: 'pending',       label: 'Pending',       icon: Clock,       count: pendingUsers.length, alert: true },
            { key: 'billing',       label: 'Billing',       icon: CreditCard,  count: null },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}{tab.count != null ? ` (${tab.count})` : ''}</span>
                  {tab.alert && tab.count > 0 && (
                    <span className="px-1.5 py-0.5 bg-yellow-500 text-black text-xs rounded-full font-bold">
                      {tab.count}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {activeTab === 'overview' && (
          <>
          <QuotaPanel quota={quota} quotaMessage={quotaMessage} />
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            {syncError && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
                <p className="text-red-400 text-sm">{syncError}</p>
                <button onClick={() => setSyncError('')} className="text-red-400 hover:text-red-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {deployments.length === 0 ? (
              <div className="py-16 text-center">
                <Server className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400 mb-1">No deployments found</p>
                <p className="text-slate-500 text-sm">Your company has no active deployments yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-800">
                      <th className="pb-3 text-slate-400 font-medium">Application</th>
                      <th className="pb-3 text-slate-400 font-medium">Namespace</th>
                      <th className="pb-3 text-slate-400 font-medium">Health</th>
                      <th className="pb-3 text-slate-400 font-medium">Sync Status</th>
                      <th className="pb-3 text-slate-400 font-medium">Date</th>
                      <th className="pb-3 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployments.map(d => (
                      <tr key={d.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="py-4 font-medium text-white">{d.name}</td>
                        <td className="py-4">
                          <span className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs font-mono">{d.namespace}</span>
                        </td>
                        <td className="py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(d.health?.status)}`}>{d.health?.status}</span>
                        </td>
                        <td className="py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSyncStatusColor(d.sync?.status)}`}>{d.sync?.status}</span>
                        </td>
                        <td className="py-4 text-slate-400">{d.deployedDate}</td>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSync(d.name)}
                              disabled={syncingApp === d.name}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${syncingApp === d.name ? 'animate-spin' : ''}`} />
                              {syncingApp === d.name ? 'Syncing...' : 'Sync'}
                            </button>
                            <button
                              onClick={() => setLogsAppName(d.name)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Logs
                            </button>
                            <button
                              onClick={() => setDeleteConfirmApp(d.name)}
                              disabled={deletingApp === d.name}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {deletingApp === d.name
                                ? <div className="w-3.5 h-3.5 border border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                : <Trash2 className="w-3.5 h-3.5" />
                              }
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
        )}

        {activeTab === 'subscriptions' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h3 className="font-semibold text-white">Your Subscriptions</h3>
              <button
                onClick={() => navigate('/pricing')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium rounded-lg transition-all text-sm"
              >
                <CreditCard className="w-4 h-4" />
                <span>New Subscription</span>
              </button>
            </div>

            {subscriptions.length === 0 ? (
              <div className="py-16 text-center">
                <CreditCard className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400 mb-1">No subscriptions yet</p>
                <p className="text-slate-500 text-sm mb-6">Subscribe to a plan to get started</p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                >
                  Browse plans
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-left">
                      <th className="px-6 py-3 text-slate-400 font-medium">Plan</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Status</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Billing</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Total</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Next Billing</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map(sub => (
                      <tr key={sub.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <PlanBadge slug={sub.plan_slug} />
                            {sub.notes && <p className="text-xs text-slate-500 mt-1">{sub.notes}</p>}
                          </div>
                        </td>
                        <td className="px-6 py-4"><StatusBadge status={sub.status} /></td>
                        <td className="px-6 py-4 text-slate-400 capitalize">{sub.billing_cycle}</td>
                        <td className="px-6 py-4 font-semibold text-white">${sub.price_total}</td>
                        <td className="px-6 py-4 text-slate-400">
                          {sub.next_billing_at
                            ? new Date(sub.next_billing_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {sub.status !== 'cancelled' ? (
                            <button
                              onClick={() => handleCancelSubscription(sub.id, sub.plan_name)}
                              disabled={cancellingId === sub.id}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {cancellingId === sub.id
                                ? <div className="w-3 h-3 border border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                : <XCircle className="w-3 h-3" />
                              }
                              Cancel
                            </button>
                          ) : (
                            <span className="text-xs text-slate-500">Cancelled</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl">
            {allUsers.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400">No users in your company yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-800">
                      <th className="px-6 py-3 text-slate-400 font-medium">Name</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Email</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Status</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Role</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Joined</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map(u => (
                      <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4 text-white font-medium">{u.name}</td>
                        <td className="px-6 py-4 text-slate-400">{u.email}</td>
                        <td className="px-6 py-4"><StatusBadge status={u.status} /></td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                            u.role === 'company_admin'
                              ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                              : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                          }`}>
                            {u.role === 'company_admin' ? 'Company Admin' : 'User'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          {u.role === 'user' && (
                            <button
                              onClick={() => handleDeleteUser(u.id, u.name)}
                              disabled={deletingUserId === u.id}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 text-slate-400 border border-slate-600 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 rounded transition-colors disabled:opacity-50"
                            >
                              {deletingUserId === u.id
                                ? <div className="w-3 h-3 border border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                                : <Trash2 className="w-3 h-3" />
                              }
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pending' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl">
            {pendingUsers.length === 0 ? (
              <div className="py-16 text-center">
                <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-4" />
                <p className="text-slate-400 mb-1">No pending approvals</p>
                <p className="text-slate-500 text-sm">All users have been reviewed</p>
              </div>
            ) : (
              <div>
                <div className="px-6 py-4 border-b border-slate-800">
                  <p className="text-sm text-slate-400">
                    {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval
                  </p>
                </div>
                <div className="divide-y divide-slate-800">
                  {pendingUsers.map(u => (
                    <div key={u.id} className="p-5 flex items-center justify-between hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-medium text-sm">{u.name?.charAt(0)?.toUpperCase() || '?'}</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{u.name}</p>
                          <p className="text-sm text-slate-400">{u.email}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Requested {new Date(u.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        <button
                          onClick={() => handleApprove(u.id)}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 rounded-lg transition-colors text-sm"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Approve</span>
                        </button>
                        <button
                          onClick={() => handleReject(u.id)}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 rounded-lg transition-colors text-sm"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>Reject</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'billing' && <BillingTab />}

      </main>

      {logsAppName && (
        <LogsModal appName={logsAppName} onClose={() => setLogsAppName(null)} />
      )}

      {deleteConfirmApp && (
        <ConfirmDeleteModal
          appName={deleteConfirmApp}
          deleting={deletingApp === deleteConfirmApp}
          onClose={() => setDeleteConfirmApp(null)}
          onConfirm={() => handleDeleteDeployment(deleteConfirmApp)}
        />
      )}

      {showAddApp && (
        <AddAppModal
          onClose={() => setShowAddApp(false)}
          onCreated={(app) => handleProvisioned(`App "${app.name}" created — repo ready.`)}
        />
      )}

      {showCreateSecrets && (
        <CreateSecretsModal
          onClose={() => setShowCreateSecrets(false)}
          onCreated={(result) => handleProvisioned(result?.message || 'Secrets created.')}
        />
      )}
    </div>
  );
};

export default CompanyAdminDashboard;